import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface RecenteRicerca {
  tipo: 'PAGINA' | 'OSPITE' | 'PERSONALE' | 'TICKET'
  titolo: string
  sottotitolo?: string | null
  icona: string
  legacy: boolean
  target: string
}

const MAX_RECENTI = 10
/** Oltre questo tempo una voce non si mostra più: i nomi non restano nel browser a tempo indeterminato. */
const SCADENZA_MS = 7 * 24 * 60 * 60 * 1000
const TIPI = ['PAGINA', 'OSPITE', 'PERSONALE', 'TICKET']

/** Un valore riletto dallo storage è dato non fidato: si accetta solo la forma attesa. */
function valida(r: unknown): r is RecenteRicerca & { ts: number } {
  if (!r || typeof r !== 'object') return false
  const o = r as Record<string, unknown>
  if (typeof o.titolo !== 'string' || typeof o.target !== 'string' || typeof o.icona !== 'string') return false
  if (typeof o.tipo !== 'string' || !TIPI.includes(o.tipo)) return false
  if (o.sottotitolo != null && typeof o.sottotitolo !== 'string') return false
  if (typeof o.ts !== 'number' || Date.now() - o.ts > SCADENZA_MS) return false
  if (o.target.length > 500 || /[\u0000-\u001f]/.test(o.target)) return false
  // Classico: il target è il valore di funzioni_2.pagina (path .xhtml o nome «bare» come `Spese`) e
  // finisce sempre codificato in `home.seam?pagina=…` sullo stesso sito: basta escludere gli indirizzi.
  if (o.legacy) return !/^[a-z][a-z0-9+.-]*:|^\/\/|^\/\\/i.test(o.target)
  // Portale nuovo: solo percorsi interni.
  if (!/^\/(?![/\\])/.test(o.target)) return false
  return true
}
const CHIAVE = 'palette-recenti'

/** Una lista per username, che resta anche dopo il logout (fino alla scadenza): su un PC condiviso un operatore non vede le ricerche dell'altro. */
function chiaveUtente(username: string | undefined): string | null {
  return username ? `${CHIAVE}:${username.toLowerCase()}` : null
}

/**
 * Stato della palette di ricerca globale (header + Ctrl/Cmd+K).
 * Il bottone di ricerca nell'header e lo shortcut chiamano open()/toggle();
 * il componente PaletteRicerca e' legato a isOpen.
 */
export const useGlobalSearchStore = defineStore('globalSearch', () => {
  const isOpen = ref(false)
  const recenti = ref<Array<RecenteRicerca & { ts?: number }>>([])
  let chiave: string | null = null

  /** Carica le recenti dell'utente che ha aperto la palette. */
  function carica(username: string | undefined): void {
    chiave = chiaveUtente(username)
    recenti.value = []
    if (!chiave) return
    try {
      const arr = JSON.parse(localStorage.getItem(chiave) ?? '[]')
      if (Array.isArray(arr)) recenti.value = arr.filter(valida).slice(0, MAX_RECENTI)
    } catch { /* storage assente o JSON rovinato: lista vuota */ }
  }

  function salva(): void {
    if (!chiave) return
    try { localStorage.setItem(chiave, JSON.stringify(recenti.value)) } catch { /* ignora */ }
  }

  /** Registra il risultato su cui l'utente ha cliccato: in cima, senza doppioni, max 10. */
  function registra(r: RecenteRicerca): void {
    const voce = { tipo: r.tipo, titolo: r.titolo, sottotitolo: r.sottotitolo ?? null, icona: r.icona, legacy: r.legacy, target: r.target, ts: Date.now() }
    recenti.value = [voce, ...recenti.value.filter((x) => !(x.tipo === voce.tipo && x.target === voce.target))].slice(0, MAX_RECENTI)
    salva()
  }

  function svuotaRecenti(): void {
    recenti.value = []
    salva()
  }

  function open(): void {
    isOpen.value = true
  }
  function close(): void {
    isOpen.value = false
  }
  function toggle(): void {
    isOpen.value = !isOpen.value
  }

  return { isOpen, recenti, carica, registra, svuotaRecenti, open, close, toggle }
})
